import styled from "styled-components";

/**
 * App-shell layout primitives (ticket #41 restyle).
 *
 * The v3 design (`Transferify.dc.html` `.app`/`.bg`) has no chrome of its own: pages paint their
 * own surfaces and the public topbar floats over them. These
 * wrappers therefore only do LAYOUT — the per-mode `paper` token on {@link Main} is just the
 * base coat behind page backdrops (and prevents a flash before they mount). The legacy v2 frame
 * (blue `palette.primary.main` body + white rounded MUI `Paper` sheet on `Content`) is
 * intentionally gone.
 */

export let AppRoot = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow: auto;
  font-size: calc(10px + 2vmin);
  text-align: center;
`;

/**
 * Transparent scroll column — pages provide their own surfaces/backdrops. Also anchors the
 * absolute, chromeless topbar (ticket #45): the bar lives INSIDE this scroll container, so on
 * scrollable pages it scrolls away with the content instead of pinning over it.
 */
export const Content = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
  overflow: auto;
  display: flex;
  flex-direction: column;
  justify-items: center;
  background-color: transparent;

  /* Room for an end-of-page control to scroll clear of the consent banner's fixed bar (ui#758; the
     document itself never scrolls, so a document-level spacer could never buy this room).

     It is SCROLLABLE OVERFLOW, never padding (ui#1587). This box has a DEFINITE height under
     border-box sizing (MUI's CssBaseline sets border-box app-wide), so bottom padding is taken out
     of the content box rather than added to the element: with the banner up the page's own height
     fell from 812 to 629 at 375x812 and its content was clipped mid-sentence. An absolutely
     positioned spacer is out of flow, so it moves nothing and paints nothing; it only pushes the
     scrollable overflow region past the content's end, which is the room ui#758 actually needed.

     consent/CookieBanner writes both variables and leaves both at 0 unless a control would really be
     stranded under the bar, so the common page reserves nothing at all. */
  &::after {
    content: "";
    position: absolute;
    top: var(--consent-clearance-top, 0px);
    left: 0;
    width: 1px;
    height: var(--consent-clearance, 0px);
    pointer-events: none;
  }
`;

/**
 * The app's outermost box, exactly one viewport tall.
 *
 * `100dvh`, not `100vh` (ui#687). On a mobile browser `100vh` is the LARGE viewport — the height the page
 * would have if the address bar were retracted — so a shell pinned to it is taller than what the visitor
 * can actually see whenever the bar is showing, and the DOCUMENT scrolls by exactly the bar's height.
 * That is page scroll produced by the chrome rather than by content, and no amount of fitting the content
 * removes it. `dvh` tracks the bar as it comes and goes, which is the whole reason the unit exists.
 *
 * The `vh` line above it is the fallback for browsers without `dvh` (pre-2022 Safari/Chrome), which take
 * the second declaration only if they understand it — so they keep exactly today's behaviour rather than
 * losing the height entirely.
 */
export let Main = styled.div`
	height: 100vh;
	height: 100dvh;
	display: flex;
	flex-direction: row;
	justify-content: flex-start;
	background-color: ${({theme}) => theme.tokens.paper};
`
